/**
 * Inflation arithmetic on the CPI-U annual averages.
 *
 * A dollar amount in `from` buys what amount × CPI(to) ÷ CPI(from) buys in `to`.
 */
import {
  CPI_U_ANNUAL,
  CPI_U_FIRST_YEAR,
  CPI_U_LAST_YEAR,
} from "@/lib/tool-kit/econ-macro/cpi-u-annual";

/** True for a whole year the CPI-U series covers. */
export function coveredYear(year: number): boolean {
  return (
    Number.isInteger(year) &&
    year >= CPI_U_FIRST_YEAR &&
    year <= CPI_U_LAST_YEAR
  );
}

/** The CPI-U annual average for a covered year. */
export function cpiOf(year: number): number {
  const value = CPI_U_ANNUAL[year];
  if (value === undefined) {
    throw new RangeError(`No CPI-U value for ${year}`);
  }
  return value;
}

/** What `amount` in year `from` is worth in year `to`, in the dollars of `to`. */
export function adjustForInflation(
  amount: number,
  from: number,
  to: number
): number {
  return (amount * cpiOf(to)) / cpiOf(from);
}

/** Total price change from `from` to `to` as a decimal; negative if `to` is earlier. */
export function cumulativeInflation(from: number, to: number): number {
  return cpiOf(to) / cpiOf(from) - 1;
}

/** The average yearly rate between two different years, as a decimal. */
export function averageAnnualInflation(
  from: number,
  to: number
): number | null {
  const years = Math.abs(to - from);
  if (years === 0) {
    return null;
  }
  const ratio = cpiOf(Math.max(from, to)) / cpiOf(Math.min(from, to));
  return ratio ** (1 / years) - 1;
}

export interface YearRow {
  year: number;
  cpi: number;
  /** Change from the previous calendar year; null for the first year of the series. */
  inflation: number | null;
  /** The starting amount restated in this year's dollars. */
  value: number;
}

/** One row per year between `from` and `to` inclusive, in ascending order. */
export function yearRows(amount: number, from: number, to: number): YearRow[] {
  const first = Math.min(from, to);
  const last = Math.max(from, to);
  const rows: YearRow[] = [];
  for (let year = first; year <= last; year++) {
    rows.push({
      year,
      cpi: cpiOf(year),
      inflation:
        year > CPI_U_FIRST_YEAR ? cpiOf(year) / cpiOf(year - 1) - 1 : null,
      value: adjustForInflation(amount, from, year),
    });
  }
  return rows;
}
